import React from 'react'
import {withPrismicPreview} from 'gatsby-plugin-prismic-previews'
import {graphql, useStaticQuery} from 'gatsby'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import Grid from '@mui/material/Grid'
import Link from '@mui/material/Link'
import Logo from 'src/components/atoms/Logo'
import NavLink from 'src/components/atoms/NavLink'
import {NavLinksAllExposed} from 'src/components/molecules/NavLinks'
import RenderRichText from 'src/utils/renderRichText'
import {diamondPattern} from 'src/assets/images'
import {Twitter, Linkedin} from 'src/assets/icons'

import {
  AllDocumentTypes,
  NavigationDocumentDataBodyLinksSlice,
  ContactDocumentDataBodyContactSlice,
} from '../../../../types.generated'
import useSlice from 'src/hooks/useSlice'

const Footer = ({data}: AllDocumentTypes) => {
  const prismicNavigation = useSlice(
    {data: data.prismicNavigation},
    'links',
  ) as NavigationDocumentDataBodyLinksSlice

  const contactData = useSlice(
    {data: data.prismicContact},
    'contact',
  ) as ContactDocumentDataBodyContactSlice

  const currentYear: number = new Date().getFullYear();
  return (
    <Box
      sx={{
        backgroundImage: `linear-gradient(to right, rgba(39,39,39,1) 0%, rgba(39,39,39,0.4) 100%), url(${diamondPattern});`,
        backgroundColor: '#272727',
        backgroundRepeat: 'repeat',
        pt: 6,
        position: 'relative',
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={3} sx={{pb: 4}}>
          <Grid item xs={12} md={3}>
            <Box sx={{width: 80, opacity: 1, mb: 4}}>
              <Logo contrast />
            </Box>
            <Typography
              variant="body1"
              sx={{color: 'primary.contrastText', opacity: 0.8, pr: 6}}
            >
              Shining a light on the true costs of trading foreign exchange.
            </Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography
              variant="h6"
              color="primary.contrastText"
              sx={{mb: 3, opacity: 0.8}}
            >
              Menu
            </Typography>
            <Stack spacing={3} sx={{opacity: 0.8}}>
              <NavLinksAllExposed
                variant="body1"
                contrast
                bold={false}
                links={prismicNavigation.items}
              />
            </Stack>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography
              variant="h6"
              color="primary.contrastText"
              sx={{mb: 3, opacity: 0.8}}
            >
              Policies
            </Typography>
            <Stack spacing={3} sx={{opacity: 0.8}}>
              <NavLink to="/terms" contrast variant="body1">
                Terms of Use
              </NavLink>
              <NavLink to="/privacy" contrast variant="body1">
                Privacy Policy
              </NavLink>
              <NavLink to="/cookies" contrast variant="body1">
                Cookie Policy
              </NavLink>
            </Stack>
          </Grid>
          <Grid item xs={12} md={3} sx={{opacity: 0.8}}>
            <Box sx={{mb: 2}}>
              <Link href="https://twitter.com/NewchangeFX" sx={{mr: 2}}>
                <Twitter />
              </Link>
              <Link href="https://www.linkedin.com/company/new-change-fx/">
                <Linkedin />
              </Link>
            </Box>
            <Box>
              <RenderRichText
                field={contactData.primary.address}
                components={{
                  paragraph: ({children}) => (
                    <Typography variant="body1" color="primary.contrastText">
                      {children}
                    </Typography>
                  ),
                }}
              />
              <Typography variant="body1" color="primary.contrastText">
                Tel: {contactData.primary.phone.text}
              </Typography>
              <Typography variant="body1" color="primary.contrastText">
                Email: {contactData.primary.email.text}
              </Typography>
            </Box>
          </Grid>
        </Grid>
        <Typography
          variant="body1"
          color="primary.contrastText"
          gutterBottom
          sx={{mb: 0, pb: 4, opacity: 0.6}}
        >
          Corporate Responsibility. NCFX adheres to the principles set out in
          the following international codes of ethical conduct: OECD Guidelines
          for Multinational Enterprises, UN Global Compact, UN Guiding
          Principles on Business and Human Rights, ILO Conventions: Child Labour
          (C138, C182); Forced Labour (CO29, C105); Discrimination (C100, C111);
          Freedom of Association (CO87, C098).
        </Typography>
      </Container>
      <Box
        sx={{textAlign: 'center', py: 1.5, backgroundColor: 'text.secondary'}}
      >
        <Typography variant="body1" color="primary.contrastText">
          © {currentYear} New Change FX. All rights reserved.
        </Typography>
      </Box>
    </Box>
  )
}

const FooterWrapper = withPrismicPreview(Footer)

const FooterWithData = () => {
  const {
    prismicContact: {data: prismicContactData},
    prismicNavigation: {data: prismicNavigationData},
  } = useStaticQuery(graphql`
    {
      prismicNavigation {
        _previewable
        data {
          body {
            ... on PrismicNavigationDataBodyLinks {
              items {
                link_id
                url {
                  text
                }
                url_external {
                  text
                }
                hide
                title {
                  text
                }
              }
              slice_type
            }
          }
        }
      }
      prismicContact {
        _previewable
        data {
          body {
            ... on PrismicContactDataBodyContact {
              primary {
                phone {
                  text
                }
                email {
                  text
                }
                address {
                  richText
                }
              }
              slice_type
            }
          }
        }
      }
    }
  `)

  return (
    <FooterWrapper
      data={{
        prismicContact: prismicContactData,
        prismicNavigation: prismicNavigationData,
      }}
    />
  )
}

export default FooterWithData
